import React, { useEffect, useState } from "react";

import Button from "~/components/Button/Button";
import LocationIcon from "~/icons/LocationIcon";
import {
  deliveryReasonToMessage,
  type DeliveryReasonCode,
} from "~/helpers/deliveryCheck";

import styles from "./DeliveryUnavailableModal.module.css";

// Exit should feel quicker than the entrance (keep in sync with the CSS).
const EXIT_MS = 180;

type Props = {
  visible: boolean;
  /** Reason code from the failed addOrder/updateOrder. */
  reason: DeliveryReasonCode | null;
  /** Whether a pickup order type exists at this store to switch to. */
  canSwitchToPickup: boolean;
  onSwitchToPickup: () => void;
  onClose: () => void;
};

/**
 * Calm, centered "this store doesn't deliver here" modal.
 *
 * Shown when adding to / updating a delivery cart fails because the store
 * cannot deliver to the cart's address (NO_DELIVERY_AVAILABLE / ROUTE_NOT_FOUND
 * / INVALID_DELIVERY_ADDRESS) — instead of letting it hit the route
 * ErrorBoundary. No shake / alarm; the primary action offers a graceful switch
 * to pickup.
 */
const DeliveryUnavailableModal: React.FC<Props> = ({
  visible,
  reason,
  canSwitchToPickup,
  onSwitchToPickup,
  onClose,
}) => {
  // Keep mounted briefly after `visible` flips to false so the exit can play.
  const [mounted, setMounted] = useState(visible);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;
    setClosing(true);
    const t = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(t);
  }, [visible, mounted]);

  // Escape to dismiss.
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, onClose]);

  if (!mounted) return null;

  const message = reason
    ? deliveryReasonToMessage(reason)
    : deliveryReasonToMessage("NO_DELIVERY_AVAILABLE");

  return (
    <div
      className={`${styles.backdrop}${closing ? ` ${styles.closing}` : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={`${styles.modal}${closing ? ` ${styles.closing}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="This store doesn't deliver here"
      >
        <span className={styles.icon}>
          <LocationIcon />
        </span>
        <span className={styles.title}>This store doesn&apos;t deliver here</span>
        <span className={styles.body}>{message}</span>
        <div className={styles.buttons}>
          {canSwitchToPickup && (
            <Button variant="primary" onClick={onSwitchToPickup}>
              Switch to Pickup
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryUnavailableModal;
